import React, { useEffect, useState } from "react";
import { Download, ExternalLink, Loader2, Receipt, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import B2BLayout from "../business/B2BLayout";
import { api, getToken } from "../api/client";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_TONE = {
    issued: "bg-amber-100 text-amber-800",
    overdue: "bg-rose-100 text-rose-700",
    paid: "bg-emerald-100 text-emerald-700",
};

const BusinessInvoices = () => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [payOpen, setPayOpen] = useState(null);
    const [paying, setPaying] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState("zoho");
    const [reference, setReference] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/b2b/invoices", { params: { page_size: 50 } });
            setInvoices(data.items || []);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const downloadPdf = async (inv) => {
        try {
            const res = await fetch(`${API_BASE}/api/b2b/invoices/${inv.id}/pdf`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            if (!res.ok) throw new Error("Could not download");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `${inv.invoice_no}.pdf`; a.click();
            setTimeout(() => URL.revokeObjectURL(url), 500);
        } catch {
            toast.error("Could not download PDF");
        }
    };

    const submitPay = async () => {
        if (!payOpen) return;
        try {
            setPaying(true);
            const { data } = await api.post(`/b2b/invoices/${payOpen.id}/pay`, {
                payment_method: paymentMethod,
                reference: reference.trim() || null,
            });
            if (data.method === "zoho" && data.redirect_url) {
                toast.success("Redirecting to Zoho Payments…");
                window.open(data.redirect_url, "_blank", "noopener,noreferrer");
            } else {
                toast.success(data.message || "Payment claim recorded");
            }
            setPayOpen(null);
            setReference("");
            await load();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Could not initiate payment");
        } finally {
            setPaying(false);
        }
    };

    return (
        <B2BLayout title="Invoices" subtitle="B2B billing">
            {() => loading ? <div className="h-64 skeleton rounded-3xl" /> : invoices.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-12 text-center" data-testid="b2b-invoices-empty">
                    <Receipt className="mx-auto h-10 w-10 text-slate-300" />
                    <p className="mt-2 text-sm text-slate-500">No invoices yet.</p>
                </div>
            ) : (
                <div className="overflow-hidden rounded-2xl border border-zinc-100 bg-white shadow-sm" data-testid="b2b-invoices-list">
                    <table className="w-full text-sm">
                        <thead className="border-b border-zinc-100 bg-zinc-50 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                            <tr>
                                <th className="px-4 py-3">Invoice #</th>
                                <th className="px-4 py-3">Issued</th>
                                <th className="px-4 py-3">Due</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3 text-right">Amount</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-50">
                            {invoices.map((i) => {
                                const status = i.computed_status || i.status;
                                return (
                                    <tr key={i.id} className="hover:bg-zinc-50" data-testid={`b2b-invoices-row-${i.id}`}>
                                        <td className="px-4 py-3 font-mono">{i.invoice_no}</td>
                                        <td className="px-4 py-3 text-xs">{new Date(i.created_at).toLocaleDateString()}</td>
                                        <td className="px-4 py-3 text-xs">{new Date(i.due_date).toLocaleDateString()}</td>
                                        <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_TONE[status] || "bg-zinc-100 text-zinc-700"}`}>{status}</span></td>
                                        <td className="px-4 py-3 text-right font-semibold">{inr(i.amount)}</td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="inline-flex gap-1">
                                                <button onClick={() => downloadPdf(i)} title="Download PDF" className="rounded-md border border-zinc-200 px-2 py-1 text-xs hover:bg-zinc-50" data-testid={`b2b-invoice-pdf-${i.id}`}>
                                                    <Download className="-mt-0.5 inline h-3 w-3" /> PDF
                                                </button>
                                                {status !== "paid" && (
                                                    <button onClick={() => setPayOpen(i)} className="rounded-md bg-brand-700 px-2 py-1 text-xs text-white hover:bg-brand-800" data-testid={`b2b-invoice-pay-${i.id}`}>
                                                        Pay now
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {payOpen && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/50 backdrop-blur-sm p-4" data-testid="b2b-pay-dialog">
                            <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-base font-semibold">Pay invoice {payOpen.invoice_no}</h3>
                                    <button onClick={() => setPayOpen(null)} className="rounded-full p-1.5 text-zinc-400 hover:bg-zinc-100"><X className="h-4 w-4" /></button>
                                </div>
                                <p className="mt-1 text-sm text-zinc-500">Amount: <span className="font-semibold text-zinc-900">{inr(payOpen.amount)}</span></p>

                                <div className="mt-4 space-y-2">
                                    <label className={`flex cursor-pointer items-start gap-2 rounded-xl border p-3 text-sm ${paymentMethod === "zoho" ? "border-brand-300 bg-brand-50" : "border-zinc-200"}`}>
                                        <input type="radio" checked={paymentMethod === "zoho"} onChange={() => setPaymentMethod("zoho")} className="mt-0.5" data-testid="b2b-pay-method-zoho" />
                                        <div>
                                            <p className="font-medium">Zoho Payments</p>
                                            <p className="text-xs text-slate-500">UPI / Cards / Net banking — opens in new tab.</p>
                                        </div>
                                    </label>
                                    <label className={`flex cursor-pointer items-start gap-2 rounded-xl border p-3 text-sm ${paymentMethod === "bank_transfer" ? "border-brand-300 bg-brand-50" : "border-zinc-200"}`}>
                                        <input type="radio" checked={paymentMethod === "bank_transfer"} onChange={() => setPaymentMethod("bank_transfer")} className="mt-0.5" data-testid="b2b-pay-method-bank" />
                                        <div className="flex-1">
                                            <p className="font-medium">Bank transfer (NEFT/RTGS/IMPS)</p>
                                            <p className="text-xs text-slate-500">Enter your UTR / reference after transferring to our bank.</p>
                                            {paymentMethod === "bank_transfer" && (
                                                <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="UTR / transaction reference" className="mt-2 w-full rounded-md border border-zinc-200 px-2 py-1 text-xs outline-none focus:border-brand-400" data-testid="b2b-pay-reference" />
                                            )}
                                        </div>
                                    </label>
                                </div>

                                <div className="mt-4 flex justify-end gap-2">
                                    <button onClick={() => setPayOpen(null)} className="rounded-full border border-zinc-200 px-4 py-2 text-sm">Cancel</button>
                                    <button onClick={submitPay} disabled={paying} className="btn-brand !py-2 !px-5 !text-sm" data-testid="b2b-pay-submit">
                                        {paying ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                                        {paying ? "Processing…" : paymentMethod === "zoho" ? "Pay via Zoho" : "Submit claim"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </B2BLayout>
    );
};

export default BusinessInvoices;
